/**
 * Which backend a discount request should use.
 *
 * The portal serves the discount feature at two routes, permanently:
 *
 *   - `/discounts`          — cloud-discount-service, always. There is no flag and no fallback
 *                             here: `REACT_APP_DISCOUNT_API_URL` and `REACT_APP_NEW_CORE_API_URL`
 *                             must be set per environment, or this page cannot load.
 *   - `/discounts-classic`  — hardwired to the legacy Selfly Cloud API, as an operator-facing
 *                             fallback. Never affected by that configuration.
 *
 * **The route is the mode.** `connected-react-router` already keeps the pathname in Redux
 * (`reducers/index.js`), so this needs no state of its own — and a mode stored separately could
 * disagree with the URL, which is a silent-wrongness surface this migration has produced enough
 * of already.
 *
 * Read `state.router` on **both** sides — the page's fetch effect and the thunks — never
 * `props.location` from `withRouter`. There are two location sources (router context and
 * `state.router`) which can momentarily differ; using the store on both sides makes "which
 * backend does the page want" and "which backend did the thunk use" provably the same answer.
 *
 * Lives in its own module rather than in `discountApi.js` because
 * `test/unit/services/discountWritePath.test.js` replaces that module with a hand-written mock
 * factory — anything added there reads back `undefined` in the existing tests.
 */

/** Route serving the legacy implementation. Permanent; see the note above. */
export const CLASSIC_DISCOUNTS_PATH = '/discounts-classic';

/** Labels recorded on `state.discounts.source` — the provenance of the loaded list. */
export const SOURCE_CDS = 'cds';
export const SOURCE_LEGACY = 'legacy';

/**
 * @param {object} state full Redux state (needs `router.location.pathname`)
 * @returns {boolean} true when this request should go to cloud-discount-service
 */
export function useDiscountService(state) {
    const pathname = (state && state.router && state.router.location && state.router.location.pathname) || '';
    // Prefix match, not equality: the route carries no `exact`, so `/discounts-classic/anything`
    // renders the classic page too. Matching only the exact path would have that URL render the
    // classic page against cds — the page and the data disagreeing about which backend they are.
    // The trailing `/` keeps a different route such as `/discounts-classic-other` out.
    return !(pathname === CLASSIC_DISCOUNTS_PATH || pathname.startsWith(`${CLASSIC_DISCOUNTS_PATH}/`));
}

/** The provenance label for a resolved backend choice. */
export function sourceLabel(useCds) {
    return useCds ? SOURCE_CDS : SOURCE_LEGACY;
}

/**
 * Whether this organization is switched to cloud-discount-service: `huskyCloudSettings.useNewDiscountApi`, the
 * flag cabinet.backend.3 also follows, so an organization's portal and cabinets move over together.
 *
 * Decides which route the Discounts tab opens and whether `/discounts` may render at all. The backend a request
 * uses is still decided by the route alone (`useDiscountService`).
 * @param {object} state full Redux state
 * @returns {boolean|null} null until it has been read for the current organization
 */
export function newDiscountApiEnabled(state) {
    const rollout = state && state.discountRollout;
    const groupId = state && state.user && state.user.group && state.user.group.Id;
    if (!rollout || !rollout.fetched || !groupId || rollout.groupId !== groupId) {
        return null;
    }
    return rollout.useNewDiscountApi === true;
}

/**
 * Where the Discounts tab leads for this organization: the classic page when it is not switched on, the cds page
 * otherwise. While the flag is still unknown that is the cds route too, which waits for the flag and redirects
 * if it turns out to be off, so a slow read never lands a switched-on organization on the classic page.
 * @param {object} state full Redux state
 * @returns {string}
 */
export function discountsHomePath(state) {
    return newDiscountApiEnabled(state) === false ? CLASSIC_DISCOUNTS_PATH : '/discounts';
}
